import React from 'react';

class Alert {
  /**
   * Show confirmation dialog
   * @param {string} message - The confirmation message
   * @param {string} title - Optional title (default: "Confirm Action")
   * @returns {Promise<boolean>} - Returns true if confirmed, false if cancelled
   */
  static async confirm(message, title = "Confirm Action", options = {}) {
    const sky = options.theme === 'sky';
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80] p-4';
      
      const dialog = document.createElement('div');
      dialog.className = sky
        ? 'bg-sky-50 border border-sky-200 rounded-lg shadow-xl p-4 sm:p-6 max-w-md w-full mx-4 transform transition-all animate-fadeIn'
        : 'bg-white rounded-lg shadow-xl p-4 sm:p-6 max-w-md w-full mx-4 transform transition-all animate-fadeIn';
      
      dialog.innerHTML = `
        <div class="flex items-start gap-3 sm:gap-4">
          <div class="flex-shrink-0">
            <svg class="w-5 h-5 sm:w-6 sm:h-6 ${sky ? 'text-sky-600' : 'text-yellow-600'}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <h3 class="text-base sm:text-lg font-semibold ${sky ? 'text-sky-950' : 'text-gray-900'} mb-2 break-words">${title}</h3>
            <p class="text-sm ${sky ? 'text-sky-800' : 'text-gray-600'} break-words">${message}</p>
          </div>
        </div>
        <div class="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4 sm:mt-6">
          <button id="confirm-btn" class="w-full sm:flex-1 ${sky ? 'bg-red-100 text-red-700 border border-red-200 hover:bg-red-200' : 'bg-red-600 text-white hover:bg-red-700'} px-4 py-2.5 sm:py-2 rounded-lg font-semibold transition text-sm sm:text-base order-2 sm:order-1">
            Confirm
          </button>
          <button id="cancel-btn" class="w-full sm:flex-1 ${sky ? 'bg-white text-sky-800 border border-sky-200 hover:bg-sky-100' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'} px-4 py-2.5 sm:py-2 rounded-lg font-semibold transition text-sm sm:text-base order-1 sm:order-2">
            Cancel
          </button>
        </div>
      `;
      
      overlay.appendChild(dialog);
      document.body.appendChild(overlay);
      
      const cleanup = () => {
        overlay.remove();
      };
      
      dialog.querySelector('#confirm-btn').onclick = () => {
        cleanup();
        resolve(true);
      };
      
      dialog.querySelector('#cancel-btn').onclick = () => {
        cleanup();
        resolve(false);
      };
      
      overlay.onclick = (e) => {
        if (e.target === overlay) {
          cleanup();
          resolve(false);
        }
      };
    });
  }

  /**
   * Prompt for required text (e.g. delete reason).
   * Resolves the trimmed string, or null if cancelled.
   */
  static async prompt(message, title = 'Enter value', options = {}) {
    const minLength = Number(options.minLength) || 0
    const confirmLabel = options.confirmLabel || 'Confirm'
    const placeholder = options.placeholder || ''

    return new Promise((resolve) => {
      const overlay = document.createElement('div')
      overlay.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'

      const dialog = document.createElement('div')
      dialog.className = 'bg-white rounded-lg shadow-xl p-4 sm:p-6 max-w-md w-full mx-4 transform transition-all animate-fadeIn'

      const safeTitle = String(title || 'Enter value')
      const safeMessage = String(message || '')

      dialog.innerHTML = `
        <div class="flex-1 min-w-0">
          <h3 class="text-base sm:text-lg font-semibold text-gray-900 mb-2 break-words"></h3>
          <p class="text-sm text-gray-600 break-words mb-3"></p>
          <textarea id="alert-prompt-input" rows="3" class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"></textarea>
          <p id="alert-prompt-error" class="hidden text-xs text-red-600 mt-1"></p>
        </div>
        <div class="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4 sm:mt-6">
          <button id="prompt-confirm-btn" class="w-full sm:flex-1 bg-red-600 text-white px-4 py-2.5 sm:py-2 rounded-lg font-semibold hover:bg-red-700 transition text-sm sm:text-base order-2 sm:order-1">
            ${confirmLabel}
          </button>
          <button id="prompt-cancel-btn" class="w-full sm:flex-1 bg-gray-200 text-gray-700 px-4 py-2.5 sm:py-2 rounded-lg font-semibold hover:bg-gray-300 transition text-sm sm:text-base order-1 sm:order-2">
            Cancel
          </button>
        </div>
      `

      dialog.querySelector('h3').textContent = safeTitle
      dialog.querySelector('p.text-sm').textContent = safeMessage
      const input = dialog.querySelector('#alert-prompt-input')
      input.placeholder = placeholder
      const errorEl = dialog.querySelector('#alert-prompt-error')

      overlay.appendChild(dialog)
      document.body.appendChild(overlay)
      setTimeout(() => input.focus(), 50)

      const cleanup = () => overlay.remove()

      dialog.querySelector('#prompt-confirm-btn').onclick = () => {
        const value = (input.value || '').trim()
        if (minLength && value.length < minLength) {
          errorEl.textContent = `Please enter at least ${minLength} characters`
          errorEl.classList.remove('hidden')
          input.focus()
          return
        }
        cleanup()
        resolve(value)
      }

      dialog.querySelector('#prompt-cancel-btn').onclick = () => {
        cleanup()
        resolve(null)
      }

      overlay.onclick = (e) => {
        if (e.target === overlay) {
          cleanup()
          resolve(null)
        }
      }
    })
  }

  /**
   * Centered alert dialog with a single OK button.
   * @param {object} [options] - { zIndex, tone: 'default'|'warning'|'error', prominent }
   */
  static async modal(message, title = 'Alert', options = {}) {
    const zIndex = Number(options.zIndex) || 50;
    const tone = options.tone || 'default';
    const prominent = Boolean(options.prominent);

    const toneStyles = {
      default: {
        dialog: 'bg-white border border-gray-200',
        icon: 'text-red-600',
        title: 'text-gray-900',
        body: 'text-gray-600',
        ok: 'bg-blue-600 hover:bg-blue-700 text-white',
      },
      warning: {
        dialog: 'bg-amber-50 border-2 border-amber-400',
        icon: 'text-amber-700',
        title: 'text-amber-950',
        body: 'text-amber-900',
        ok: 'bg-amber-700 hover:bg-amber-800 text-white',
      },
      error: {
        dialog: 'bg-red-50 border-2 border-red-400',
        icon: 'text-red-700',
        title: 'text-red-950',
        body: 'text-red-900',
        ok: 'bg-red-700 hover:bg-red-800 text-white',
      },
    };
    const style = toneStyles[tone] || toneStyles.default;
    const widthClass = prominent ? 'max-w-lg sm:max-w-xl' : 'max-w-md';
    const bodySize = prominent ? 'text-base sm:text-lg leading-relaxed' : 'text-sm';

    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'fixed inset-0 bg-black/60 flex items-center justify-center p-4';
      overlay.style.zIndex = String(zIndex);

      const dialog = document.createElement('div');
      dialog.className = `${style.dialog} rounded-xl shadow-2xl p-5 sm:p-7 ${widthClass} w-full mx-4 transform transition-all animate-fadeIn`;

      const safeTitle = String(title || 'Alert');
      const safeMessage = String(message || '');

      dialog.innerHTML = `
        <div class="flex items-start gap-3 sm:gap-4">
          <div class="flex-shrink-0 mt-0.5">
            <svg class="${prominent ? 'w-8 h-8 sm:w-9 sm:h-9' : 'w-5 h-5 sm:w-6 sm:h-6'} ${style.icon}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <h3 class="${prominent ? 'text-lg sm:text-xl' : 'text-base sm:text-lg'} font-bold ${style.title} mb-2 break-words"></h3>
            <p class="${bodySize} ${style.body} break-words"></p>
          </div>
        </div>
        <div class="flex justify-center sm:justify-end mt-5 sm:mt-7">
          <button id="alert-modal-ok" class="min-w-[8rem] ${style.ok} px-5 py-2.5 sm:py-3 rounded-lg font-semibold transition text-sm sm:text-base">
            OK
          </button>
        </div>
      `;

      dialog.querySelector('h3').textContent = safeTitle;
      dialog.querySelector('p').textContent = safeMessage;

      overlay.appendChild(dialog);
      document.body.appendChild(overlay);

      const cleanup = () => {
        overlay.remove();
        resolve();
      };

      dialog.querySelector('#alert-modal-ok').onclick = cleanup;
      overlay.onclick = (e) => {
        if (e.target === overlay) cleanup();
      };
    });
  }

  /** Center-screen warning for QR dispatch / route mismatch (above camera overlay). */
  static routeDispatchBlocked(message) {
    return this.modal(
      message || 'This shipment cannot be dispatched on the selected route and vehicle.',
      'Route not compatible',
      { zIndex: 120, tone: 'warning', prominent: true },
    );
  }

  static isRouteDispatchMessage(message) {
    return /cannot be dispatched on this route/i.test(String(message || ''));
  }

  /**
   * Show success message
   * @param {string} message - The success message
   * @param {number} duration - Display duration in ms (default: 3000)
   */
  static success(message, duration = 3000) {
    this._showToast(message, 'success', duration);
  }

  /**
   * Show error message
   * @param {string} message - The error message
   * @param {number} duration - Display duration in ms (default: 4000)
   */
  static error(message, duration = 4000) {
    this._showToast(message, 'error', duration);
  }

  /**
   * Show info message
   * @param {string} message - The info message
   * @param {number} duration - Display duration in ms (default: 3000)
   */
  static info(message, duration = 3000) {
    this._showToast(message, 'info', duration);
  }

  /**
   * Internal method to show toast notifications
   */
  static _showToast(message, type, duration) {
    const toast = document.createElement('div');
    toast.className = 'fixed top-4 right-4 left-4 sm:left-auto sm:right-4 z-50 transform transition-all duration-300 translate-x-0 animate-slideIn';
    
    const colors = {
      success: 'bg-green-500',
      error: 'bg-red-500',
      info: 'bg-blue-500'
    };
    
    const icons = {
      success: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />`,
      error: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />`,
      info: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />`
    };
    
    toast.innerHTML = `
      <div class="${colors[type]} text-white px-4 py-3 sm:px-6 sm:py-4 rounded-lg shadow-lg flex items-center gap-2 sm:gap-3 max-w-md mx-auto sm:mx-0">
        <svg class="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          ${icons[type]}
        </svg>
        <p class="font-medium text-sm sm:text-base break-words flex-1">${message}</p>
      </div>
    `;
    
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.style.transform = 'translateX(400px)';
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 300);
    }, duration);
  }
}

// Add animations to document if not already present
if (typeof document !== 'undefined') {
  const styleId = 'alert-animations';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: scale(0.95);
        }
        to {
          opacity: 1;
          transform: scale(1);
        }
      }
      @keyframes slideIn {
        from {
          transform: translateY(-100%);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }
      .animate-fadeIn {
        animation: fadeIn 0.2s ease-out;
      }
      .animate-slideIn {
        animation: slideIn 0.3s ease-out;
      }
    `;
    document.head.appendChild(style);
  }
}

export default Alert;